import { Mail, X } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Divider } from 'components/layout';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { cn } from 'utils/shadcn';

import { EmailRecipient } from './types';

interface EmailPillTooltipProps {
  recipient: EmailRecipient;
  onRemove?: (email: string) => void;
  disabled?: boolean;
}

export default function EmailPillTooltip({
  recipient,
  onRemove,
  disabled = false,
}: EmailPillTooltipProps) {
  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRemove) {
      onRemove(recipient.email);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (onRemove) {
        onRemove(recipient.email);
      }
    }
  };

  const displayText = recipient.name || recipient.email;
  const hasMetadata =
    recipient.metadata && Object.keys(recipient.metadata).length > 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Flex
          align='center'
          gap='sm'
          tabIndex={0}
          aria-label={`Selected recipient: ${displayText}.${!disabled ? ' Click to see details or press X to remove.' : ''}`}
          className={cn(
            'max-w-full border px-2 py-1 rounded-md',
            disabled
              ? 'border-neutral-300 bg-neutral-100 text-neutral-500 cursor-not-allowed'
              : 'border-neutral-400 hover:bg-neutral-100 text-neutral-800 cursor-default'
          )}
        >
          <Typography variant='body-xs' className='truncate'>
            {displayText}
          </Typography>
          {!disabled && onRemove && (
            <button
              type='button'
              aria-label={`Remove ${displayText}`}
              onClick={handleRemove}
              onKeyDown={handleKeyDown}
              className='text-neutral-600 hover:text-neutral-900 focus:outline-none hover:cursor-pointer'
            >
              <X size={14} />
            </button>
          )}
        </Flex>
      </TooltipTrigger>
      <TooltipContent>
        <div className='max-w-[280px]'>
          {recipient.name && (
            <>
              <Typography
                variant='body-sm'
                weight='semibold'
                className='text-neutral-800 truncate mb-1'
              >
                {recipient.name}
              </Typography>
              <Divider variant='strong' />
            </>
          )}

          <Flex direction='col' gap='xs' className='py-1'>
            <Flex align='center' gap='xs'>
              <Mail size={10} />
              <Typography variant='body-xs' className='text-neutral-600'>
                {recipient.email}
              </Typography>
            </Flex>
          </Flex>

          {hasMetadata && (
            <div className='py-1 border-t border-neutral-200'>
              {Object.entries(recipient.metadata!).map(([key, value]) => (
                <Flex
                  key={key}
                  justify='between'
                  align='center'
                  gap='lg'
                  className='mb-1'
                >
                  <Typography
                    variant='body-xs'
                    className='text-neutral-400 capitalize'
                  >
                    {key}:
                  </Typography>
                  <Typography variant='body-xs' className='text-neutral-800'>
                    {value}
                  </Typography>
                </Flex>
              ))}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
